import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { errorCode, errorMessage } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import {
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@/common/Modal.styles';
import { ModalCloseButton } from '@/common/ModalCloseButton';
import { formatLongDate } from '~/utils/dateFormat';

type StudentAdoptionInfo = components['schemas']['StudentAdoptionInfo'];
export type BoardTarget = components['schemas']['BoardTarget'];

interface StudentBoardModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Lets a body whose last control opens a dropdown (the assign picker) paint
   * outside the scroll box. `ModalBody`'s `overflow-y: auto` otherwise clips it,
   * which is the same reason `IntroduceYourselfModal` overrides it.
   */
  overflowVisible?: boolean;
}

/**
 * The shell both board modals share: legacy's `interactive_popup` renders a
 * titled dialog whose footer is either the confirm button or a close button
 * (`AdoptStudentInteractivePopup.php:30-36` / `:54-57`), and the success step
 * replaces the body rather than opening a second dialog.
 */
export function StudentBoardModal({
  title,
  onClose,
  children,
  footer,
  overflowVisible = false,
}: StudentBoardModalProps) {
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
          <ModalCloseButton onClick={onClose} />
        </ModalHeader>
        <Body $overflowVisible={overflowVisible}>{children}</Body>
        {footer !== undefined && <ModalFooter>{footer}</ModalFooter>}
      </ModalContent>
    </ModalOverlay>
  );
}

interface StudentInfoBlockProps {
  info: StudentAdoptionInfo;
  /**
   * The target population this surface offers: `original_targets` for adopt
   * (the picker keeps the passed-on ones), `available_targets` for pass-on.
   */
  targets: readonly BoardTarget[];
  /** Rendered inside the Matched Web Ad Targets cell when the rule below allows it. */
  renderTargets: (targets: readonly BoardTarget[]) => ReactNode;
}

/**
 * The five-row information block both confirm modals render, from one payload
 * builder in legacy too (`student-board/helpers.php:13-48`, rendered by
 * `templates/adoption_info.php:1-73` and `passon_info.php:1-72`). Every value is
 * a resolved name, never an id — captured live on staging (§7 L20).
 */
export function StudentInfoBlock({
  info,
  targets,
  renderTargets,
}: StudentInfoBlockProps) {
  const { t, i18n } = useTranslation();

  // The two attribution rows are MUTUALLY EXCLUSIVE, and that rule lives only in
  // the legacy template: `adoption_info.php:50` guards the whole web-ad cell on
  // `! $matched_campaign && …` and prints N/A otherwise, and the fullapp twin
  // encodes the same rule as validation (HasAdoptionActions.php:26-31). Rendering
  // both would offer a picker legacy never shows and let the client post two ids
  // into AdoptionTargetConflictError. Measured: 0 of 278 active board rows carry
  // both a campaign and an available web-ad match, so this suppresses nothing
  // today and will the first time a campaign lead also falls inside a target.
  const showTargets = info.matched_campaign === null && targets.length > 0;

  return (
    <>
      <InfoHeading>{t('lessons.student-information')}</InfoHeading>
      <InfoTable>
        <tbody>
          <tr>
            <th>{t('lessons.student-name-2')}</th>
            <td>{info.display_name}</td>
          </tr>
          <tr>
            <th>{t('wbs.registered-at-2')}</th>
            {/* Inside translated copy, so the date is formatted in the reader's
                language rather than the board column's en-US default. */}
            <td>{formatLongDate(info.registered, i18n.resolvedLanguage)}</td>
          </tr>
          <tr>
            <th>{t('wbs.intro-completed-2')}</th>
            <td>{info.intro_completed ? t('wbs.yes') : t('wbs.no')}</td>
          </tr>
          <tr>
            <th>{t('wbs.matched-campaign-3')}</th>
            <td>
              {info.matched_campaign ? (
                <p>
                  {t('wbs.registered-through-your-campaign', {
                    name: info.matched_campaign.campaign_name,
                    url: info.matched_campaign.campaign_url,
                  })}
                </p>
              ) : (
                t('wbs.n-a')
              )}
            </td>
          </tr>
          <tr>
            <th>{t('wbs.matched-web-ad-targets')}</th>
            <td>
              {showTargets ? (
                <>
                  {renderTargets(targets)}
                  {info.other_group_target_count > 0 && (
                    <p>
                      {t('wbs.this-student-also-matched-count', {
                        count: info.other_group_target_count,
                      })}
                    </p>
                  )}
                </>
              ) : (
                t('wbs.n-a')
              )}
            </td>
          </tr>
        </tbody>
      </InfoTable>
    </>
  );
}

/** `{Group}: {Target}` — legacy prints the GROUP's name, then the target's. */
export function targetLabel(target: BoardTarget): string {
  return `${target.group_name}: ${target.title}`;
}

interface AdoptionErrorContent {
  /** `null` keeps the modal's own title — an unrecognised failure is not a refusal. */
  title: string | null;
  body: string;
}

/**
 * The one `error_code -> body` map both modals use, for both `GET /adoption-info`
 * and `POST /adopt`. `/adoption-info` answering the ladder's typed 4xx *instead
 * of* the payload is legacy's own shape: `verify()` returns an error popup
 * rather than the confirm body (`AdoptStudentInteractivePopup.php:20-21`), so
 * the modal has one error path and not two.
 */
export function useAdoptionErrorContent(
  error: unknown,
  studentName: string,
): AdoptionErrorContent {
  const { t } = useTranslation();

  switch (errorCode(error)) {
    case 'adoption_daily_limit_exceeded':
      return {
        title: t('wbs.daily-limit-exceeded'),
        // The limit itself is per-helper server state that the no-arg
        // `AdoptionDailyLimitExceededError` does not carry and no v3 endpoint
        // exposes, so `{{limit}}` resolves to nothing and the doubled space is
        // squeezed. Costs nobody a number today: `daily_adoption_limit` is NULL
        // on 202 of 202 helpers, so the code cannot fire (W3.5 spec §10 R2).
        body: t('wbs.you-have-exceeded-your-adoption-limit', {
          limit: undefined,
        }).replace(/\s{2,}/g, ' '),
      };
    case 'student_already_adopted':
      return {
        title: t('lessons.student-already-adopted'),
        body: t('wbs.sorry-this-student-has-already-been-adopted'),
      };
    case 'student_not_adoptable':
    // The assign ladder's own refusals (§3.6) have no legacy popup either:
    // legacy's assign gates die inside `wp_send_json_error` with an untranslated
    // slug, so they reuse the same wording, which already reads
    // "Unable to assign/adopt the student - {name}".
    case 'assign_not_coordinator':
    case 'assign_target_not_study_helper':
    case 'assign_target_no_study_group':
    case 'assign_target_not_group_leader':
    case 'assign_posted_group_not_admin':
    // The three target codes are fullapp-only and have no legacy popup, so they
    // reuse the generic refusal rather than minting three unlocalisable strings.
    case 'adoption_target_conflict':
    case 'adoption_target_required':
    case 'adoption_target_not_matched':
      return {
        title: t('wbs.adoption-not-allowed'),
        body: t('wbs.unable-to-assign-adopt-the-student', {
          name: studentName,
        }),
      };
    default:
      return {
        title: null,
        body: errorMessage(error, t('wbs.something-went-wrong-please')),
      };
  }
}

const Body = styled(ModalBody)<{ $overflowVisible: boolean }>`
  overflow: ${({ $overflowVisible }) => ($overflowVisible ? 'visible' : 'auto')};
`;

/** The `<h4>` above each of legacy's two popup info tables. */
export const InfoHeading = styled.h4`
  color: var(--grey-900);
  font-family: var(--raleway);
  font-size: 16px;
  margin: 0 0 12px 0;
`;

/**
 * `student-board-popup-info-table`, the two-column label/value table both
 * `adoption_info.php` and `assign_info.php` render.
 */
export const InfoTable = styled.table`
  border-collapse: collapse;
  font-family: var(--raleway);
  font-size: 14px;
  text-align: left;
  width: 100%;

  th,
  td {
    padding: 6px 12px 6px 0;
    vertical-align: top;
  }

  th {
    color: var(--grey-700);
    font-weight: 500;
    white-space: nowrap;
    width: 1%;
  }

  p {
    margin: 0 0 8px 0;
  }

  ul {
    margin: 0;
    padding-left: 18px;
  }
`;

export const ModalParagraph = styled.p`
  color: var(--grey-900);
  font-family: var(--raleway);
  font-size: 15px;
  line-height: 1.7;
  margin: 0 0 20px 0;
`;

export const TargetSelect = styled.select`
  border: 1px solid var(--grey-400);
  border-radius: 4px;
  font-family: var(--raleway);
  font-size: 14px;
  max-width: 100%;
  padding: 6px 8px;
`;
